import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { uploadAPI } from '../api/upload';
import { isNative, selectPhoto, uriToFile } from '../utils/capacitor';
import './UploadPage.css';

const MAX_IMAGES = 50; // 이미지 최대 50개
const MAX_VIDEOS = 1;  // 비디오 최대 1개

const UploadPage = () => {
  const [uploadType, setUploadType] = useState('image'); // 'image' or 'video'
  const [files, setFiles] = useState([]); // 여러 파일
  const [previews, setPreviews] = useState([]); // 여러 미리보기
  const [currentIndex, setCurrentIndex] = useState(0); // 현재 보고 있는 파일 인덱스
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState([]); // 해시태그 배열
  const [hashtagInput, setHashtagInput] = useState(''); // 해시태그 입력 필드
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [isProcessingFiles, setIsProcessingFiles] = useState(false); // 파일 처리 중 로딩
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 }); // 처리 진행률

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const uploadMutation = useMutation(
    async () => {
      if (files.length === 0) throw new Error('파일을 선택해주세요.');

      // 여러 파일을 하나의 게시물로 업로드
      const response = await uploadAPI.multiUpload(
        files,
        caption.trim(),
        hashtags,
        'auto', // 원본 비율 유지
        setUploadProgress
      );

      return response;
    },
    {
      onSuccess: async () => {
        // 캐시 무효화 및 리페치 (await로 완료 대기)
        await queryClient.invalidateQueries('feed');
        await queryClient.invalidateQueries('explore');
        // 리페치가 완료된 후 피드로 이동
        navigate('/feed');
      },
      onError: (err) => {
        setError(err.response?.data?.message || '업로드에 실패했습니다.');
        setUploadProgress(0);
      },
    }
  );

  const handleFilesChange = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    await processFiles(selectedFiles);
    e.target.value = ''; // 같은 파일 다시 선택 가능하도록 초기화
  };

  const handleNativePhotoSelect = async () => {
    if (!isNative()) return;

    try {
      const photo = await selectPhoto();
      if (!photo) return;

      // Capacitor 이미지를 File 객체로 변환
      const file = await uriToFile(photo.webPath, 'photo.jpg');
      await processFiles([file]);
    } catch (error) {
      console.error('사진 선택 실패:', error);
      setError('사진 선택에 실패했습니다.');
    }
  };

  const processFiles = async (selectedFiles, appendMode = false) => {
    // 로딩 시작
    setIsProcessingFiles(true);
    setProcessingProgress({ current: 0, total: selectedFiles.length });
    setError('');

    const newFiles = appendMode ? [...files] : [];
    const newPreviews = appendMode ? [...previews] : [];

    // 업로드 타입에 따른 검증
    if (uploadType === 'image') {
      // 이미지 모드: 이미지만 허용, 최대 50개
      const imageFiles = selectedFiles.filter(f => f.type.startsWith('image/'));
      
      if (imageFiles.length !== selectedFiles.length) {
        const errorMsg = '이미지 모드에서는 이미지 파일만 선택할 수 있습니다.';
        setError(errorMsg);
        setIsProcessingFiles(false);
        alert(errorMsg); // 명확한 알림
        return;
      }

      if (imageFiles.length > MAX_IMAGES) {
        const errorMsg = `이미지는 최대 ${MAX_IMAGES}개까지 업로드할 수 있습니다.\n현재 선택: ${imageFiles.length}개`;
        setError(errorMsg);
        setIsProcessingFiles(false);
        alert(errorMsg); // 명확한 알림
        return;
      }
    } else {
      // 비디오 모드: 비디오만 허용, 최대 1개
      const videoFiles = selectedFiles.filter(f => f.type.startsWith('video/'));
      
      if (videoFiles.length !== selectedFiles.length) {
        const errorMsg = '비디오 모드에서는 비디오 파일만 선택할 수 있습니다.';
        setError(errorMsg);
        setIsProcessingFiles(false);
        alert(errorMsg); // 명확한 알림
        return;
      }

      if (videoFiles.length > MAX_VIDEOS) {
        const errorMsg = `비디오는 ${MAX_VIDEOS}개만 업로드할 수 있습니다.\n현재 선택: ${videoFiles.length}개`;
        setError(errorMsg);
        setIsProcessingFiles(false);
        alert(errorMsg); // 명확한 알림
        return;
      }
    }

    let processedCount = 0;
    for (const selectedFile of selectedFiles) {
      // 파일 타입 검증
      if (!selectedFile.type.startsWith('image/') && !selectedFile.type.startsWith('video/')) {
        setError('이미지 또는 영상 파일만 업로드할 수 있습니다.');
        continue;
      }

      // 파일 크기 검증 (이미지/비디오 모두 5GB)
      const maxSize = 5 * 1024 * 1024 * 1024; // 5GB
      
      if (selectedFile.size > maxSize) {
        setError('파일 크기는 5GB를 초과할 수 없습니다.');
        continue;
      }

      // 이미지 회전 처리 제거: 브라우저가 EXIF를 자동으로 처리
      // Canvas로 처리하면 EXIF 정보가 손실되므로 원본 그대로 사용
      newFiles.push(selectedFile);

      // 미리보기 생성
      const preview = await createPreview(selectedFile);
      newPreviews.push({
        url: preview,
        type: selectedFile.type,
      });

      // 진행률 업데이트
      processedCount++;
      setProcessingProgress({ current: processedCount, total: selectedFiles.length });
    }

    // 로딩 종료
    setIsProcessingFiles(false);

    if (newFiles.length === 0) return;

    setError('');
    setFiles(newFiles);
    setPreviews(newPreviews);
    if (!appendMode) {
      setCurrentIndex(0);
    }

    // 큰 파일 안내 메시지 (1GB 이상)
    const totalSize = newFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > 1024 * 1024 * 1024) {
      const totalSizeGB = (totalSize / (1024 * 1024 * 1024)).toFixed(2);
      alert(`📁 선택한 파일 크기: ${totalSizeGB}GB\n\n업로드 시 인터넷 속도에 따라 시간이 걸릴 수 있습니다.`);
    }
  };

  const createPreview = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (files.length === 0) {
      setError('파일을 선택해주세요.');
      return;
    }

    // 총 파일 크기 계산
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(1);
    const totalSizeGB = (totalSize / (1024 * 1024 * 1024)).toFixed(2);

    // 큰 파일 경고 (500MB 이상)
    if (totalSize > 500 * 1024 * 1024) {
      const sizeText = totalSize > 1024 * 1024 * 1024 ? `${totalSizeGB}GB` : `${totalSizeMB}MB`;
      const confirmed = window.confirm(
        `업로드할 파일 크기가 ${sizeText}입니다.\n` +
        `인터넷 속도에 따라 업로드에 시간이 걸릴 수 있습니다.\n\n` +
        `계속하시겠습니까?`
      );
      
      if (!confirmed) {
        return;
      }
    }

    uploadMutation.mutate();
  };

  const handleReset = () => {
    setFiles([]);
    setPreviews([]);
    setCurrentIndex(0);
    setCaption('');
    setHashtags([]);
    setHashtagInput('');
    setUploadProgress(0);
    setError('');
  };

  // 해시태그 추가
  const handleAddHashtag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = hashtagInput.trim().replace(/^#/, ''); // # 제거
      
      if (tag && !hashtags.includes(tag)) {
        if (hashtags.length >= 30) {
          setError('해시태그는 최대 30개까지 추가할 수 있습니다.');
          return;
        }
        setHashtags([...hashtags, tag]);
        setHashtagInput('');
        setError('');
      }
    }
  };

  // 해시태그 삭제
  const handleRemoveHashtag = (tagToRemove) => {
    setHashtags(hashtags.filter(tag => tag !== tagToRemove));
  };

  const handlePrevImage = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : previews.length - 1));
  };

  const handleNextImage = () => {
    setCurrentIndex((prev) => (prev < previews.length - 1 ? prev + 1 : 0));
  };

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    setFiles(newFiles);
    setPreviews(newPreviews);

    if (currentIndex >= newPreviews.length && newPreviews.length > 0) {
      setCurrentIndex(newPreviews.length - 1);
    } else if (newPreviews.length === 0) {
      setCurrentIndex(0);
    }
  };

  // 추가 파일 선택 핸들러
  const handleAddMoreFiles = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    // 현재 + 추가 = 총 개수 체크
    const totalCount = files.length + selectedFiles.length;
    
    if (uploadType === 'image') {
      if (totalCount > MAX_IMAGES) {
        const errorMsg = `이미지는 최대 ${MAX_IMAGES}개까지 업로드할 수 있습니다.\n현재: ${files.length}개, 추가: ${selectedFiles.length}개`;
        setError(errorMsg);
        alert(errorMsg);
        // input value 초기화
        e.target.value = '';
        return;
      }
    }

    // 파일 처리 (추가 모드)
    await processFiles(selectedFiles, true);
    // input value 초기화 (같은 파일 다시 선택 가능하도록)
    e.target.value = '';
  };

  return (
    <div className="upload-container">
      {/* 파일 처리 중 로딩 오버레이 */}
      {isProcessingFiles && (
        <div className="processing-overlay">
          <div className="processing-spinner">
            <div className="spinner"></div>
            <p className="processing-text">
              이미지 처리 중... ({processingProgress.current}/{processingProgress.total})
            </p>
            <p className="processing-hint">잠시만 기다려주세요</p>
          </div>
        </div>
      )}

      <div className="upload-box">
        <h2 className="upload-title">새 게시물 만들기</h2>

        {/* 업로드 타입 선택 탭 */}
        {previews.length === 0 && (
          <div className="upload-type-tabs">
            <button
              type="button"
              className={`tab-button ${uploadType === 'image' ? 'active' : ''}`}
              onClick={() => setUploadType('image')}
            >
              📷 이미지 (최대 {MAX_IMAGES}개)
            </button>
            <button
              type="button"
              className={`tab-button ${uploadType === 'video' ? 'active' : ''}`}
              onClick={() => setUploadType('video')}
            >
              🎬 비디오 (최대 {MAX_VIDEOS}개)
            </button>
          </div>
        )}

        {previews.length === 0 ? (
          <div className="upload-dropzone">
            {isNative() ? (
              // 네이티브 앱: 버튼으로 사진 선택
              <div className="native-photo-select">
                <button 
                  type="button"
                  onClick={handleNativePhotoSelect}
                  className="select-photo-btn"
                >
                  <svg width="96" height="77" viewBox="0 0 96 77" fill="currentColor">
                    <path d="M80.5 7.5h-65C10.3 7.5 6 11.8 6 17v43c0 5.2 4.3 9.5 9.5 9.5h65c5.2 0 9.5-4.3 9.5-9.5V17c0-5.2-4.3-9.5-9.5-9.5zM69 27c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm14 32H13V17h70v42zm-49-20l12 15 21-28 16 21" />
                  </svg>
                  <p>사진 또는 영상 선택</p>
                  <p className="file-hint">카메라 촬영 또는 앨범에서 선택</p>
                </button>
              </div>
            ) : (
              // 웹: 파일 선택
              <>
                <input
                  type="file"
                  id="file-input"
                  accept={uploadType === 'image' ? 'image/*' : 'video/*'}
                  multiple={uploadType === 'image'}
                  onChange={handleFilesChange}
                  className="file-input"
                />
                <label htmlFor="file-input" className="file-label">
                  <svg width="96" height="77" viewBox="0 0 96 77" fill="currentColor">
                    <path d="M80.5 7.5h-65C10.3 7.5 6 11.8 6 17v43c0 5.2 4.3 9.5 9.5 9.5h65c5.2 0 9.5-4.3 9.5-9.5V17c0-5.2-4.3-9.5-9.5-9.5zM69 27c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm14 32H13V17h70v42zm-49-20l12 15 21-28 16 21" />
                  </svg>
                  <p>
                    {uploadType === 'image' 
                      ? '이미지를 선택하세요' 
                      : '비디오를 선택하세요'}
                  </p>
                  <p className="file-hint">
                    {uploadType === 'image'
                      ? `최대 ${MAX_IMAGES}개까지 선택 가능, 각 5GB 이하`
                      : `최대 ${MAX_VIDEOS}개, 5GB 이하`}
                  </p>
                </label>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="upload-form">
            <div className="preview-container-multi">
              {/* 메인 미리보기 */}
              <div className="main-preview">
                {previews[currentIndex].type.startsWith('image/') ? (
                  <img 
                    src={previews[currentIndex].url} 
                    alt={`preview ${currentIndex + 1}`} 
                    className="preview-media" 
                  />
                ) : (
                  <video 
                    src={previews[currentIndex].url} 
                    controls 
                    className="preview-media" 
                  />
                )}

                {/* 이전/다음 버튼 (2개 이상일 때만) */}
                {previews.length > 1 && (
                  <>
                    <button 
                      type="button" 
                      onClick={handlePrevImage} 
                      className="carousel-btn carousel-btn-prev"
                      aria-label="이전 이미지"
                    >
                      ‹
                    </button>
                    <button 
                      type="button" 
                      onClick={handleNextImage} 
                      className="carousel-btn carousel-btn-next"
                      aria-label="다음 이미지"
                    >
                      ›
                    </button>

                    {/* 인디케이터 (5개 이하만 도트 표시) */}
                    {previews.length <= 5 ? (
                      <div className="carousel-indicators">
                        {previews.map((_, index) => (
                          <span 
                            key={index} 
                            className={`indicator ${index === currentIndex ? 'active' : ''}`}
                            onClick={() => setCurrentIndex(index)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="media-counter">
                        {currentIndex + 1} / {previews.length}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* 썸네일 리스트 */}
              {previews.length >= 1 && (
                <>
                  <div className="thumbnails-container">
                    {previews.map((preview, index) => (
                      <div 
                        key={index} 
                        className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
                        onClick={() => setCurrentIndex(index)}
                      >
                        {preview.type.startsWith('image/') ? (
                          <img src={preview.url} alt={`thumb ${index + 1}`} />
                        ) : (
                          <video src={preview.url} />
                        )}
                        <button
                          type="button"
                          className="remove-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(index);
                          }}
                          aria-label="삭제"
                        >
                          ×
                        </button>
                        <span className="thumbnail-number">{index + 1}</span>
                      </div>
                    ))}
                    
                    {/* 추가 선택 버튼 (이미지 모드이고 50개 미만일 때) */}
                    {uploadType === 'image' && files.length < MAX_IMAGES && (
                      <div className="thumbnail add-more-thumbnail">
                        <input
                          type="file"
                          id="add-more-input"
                          accept="image/*"
                          multiple
                          onChange={handleAddMoreFiles}
                          className="file-input"
                          style={{ display: 'none' }}
                        />
                        <label htmlFor="add-more-input" className="add-more-label">
                          <svg width="48" height="48" viewBox="0 0 48 48" fill="currentColor">
                            <path d="M24 10v28M10 24h28" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                          </svg>
                          <span>추가</span>
                        </label>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="upload-details">
              <textarea
                placeholder="설명을 입력하세요..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="caption-input"
                rows={4}
                maxLength={2000}
              />

              {/* 해시태그 입력 */}
              <div className="hashtag-section">
                <input
                  type="text"
                  placeholder="해시태그 입력 (Enter 또는 , 로 추가)"
                  value={hashtagInput}
                  onChange={(e) => setHashtagInput(e.target.value)}
                  onKeyDown={handleAddHashtag}
                  className="hashtag-input"
                  maxLength={50}
                />
                {hashtags.length > 0 && (
                  <div className="hashtag-list">
                    {hashtags.map((tag, index) => (
                      <span key={index} className="hashtag-tag">
                        #{tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveHashtag(tag)}
                          className="hashtag-remove"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="file-info">
                <p className="file-count">{files.length}개의 파일</p>
                <p className="file-size">
                  {(() => {
                    const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
                    const totalMB = totalBytes / (1024 * 1024);
                    const totalGB = totalBytes / (1024 * 1024 * 1024);
                    
                    if (totalGB >= 1) {
                      return `총 ${totalGB.toFixed(2)} GB`;
                    } else {
                      return `총 ${totalMB.toFixed(2)} MB`;
                    }
                  })()}
                </p>
              </div>

              {error && <div className="error-message">{error}</div>}

              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="progress-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="progress-text">
                    {uploadProgress}% 업로드 중...
                    {(() => {
                      const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
                      const totalGB = totalBytes / (1024 * 1024 * 1024);
                      if (totalGB >= 1) {
                        return ` (파일이 커서 시간이 걸릴 수 있습니다)`;
                      }
                      return '';
                    })()}
                  </p>
                </div>
              )}

              <div className="button-group">
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn btn-secondary"
                  disabled={uploadMutation.isLoading}
                >
                  다시 선택
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={uploadMutation.isLoading || files.length === 0}
                >
                  {uploadMutation.isLoading ? '업로드 중...' : '공유하기'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default UploadPage;
